import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { users } from '../api';

const MIN_LENGTH = 12;

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }
    if (password.length < MIN_LENGTH) {
      setError(`Das Passwort muss mindestens ${MIN_LENGTH} Zeichen lang sein.`);
      return;
    }

    setLoading(true);
    try {
      await users.resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Zurücksetzen fehlgeschlagen.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-sm">
        <img src="/juni-logo.svg" alt="juni" className="h-8 mx-auto mb-8" />
        <div className="bg-surface border border-slate-200 rounded-xl shadow-sm p-6">
          {done ? (
            <div className="text-center py-4">
              <CheckCircle className="mx-auto text-brand-600 mb-3" size={40} />
              <h1 className="text-lg font-semibold text-slate-900 mb-1">{t('auth.passwordSet')}</h1>
              <p className="text-sm text-slate-500">{t('auth.redirectingToLogin')}</p>
            </div>
          ) : !token ? (
            <div className="text-center py-4">
              <h1 className="text-lg font-semibold text-slate-900 mb-1">{t('auth.invalidLink')}</h1>
              <p className="text-sm text-slate-500 mb-5">
                Dieser Link enthält kein gültiges Token.
              </p>
              <Link to="/forgot-password" className="text-brand-600 hover:text-brand-800 text-sm">
                Neuen Link anfordern
              </Link>
            </div>
          ) : (
            <>
              <h1 className="text-lg font-semibold text-slate-900 mb-1">{t('auth.setPassword')}</h1>
              <p className="text-sm text-slate-500 mb-5">Mindestens {MIN_LENGTH} Zeichen.</p>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                    {error}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Neues Passwort
                  </label>
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Passwort bestätigen
                  </label>
                  <input
                    type="password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-brand-600 hover:bg-brand-700 text-white font-medium text-sm py-2.5 rounded-lg transition-colors disabled:opacity-60"
                >
                  {loading ? 'Wird gespeichert…' : 'Passwort festlegen'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
